import { useState } from 'react';
import { Activity, Loader2, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	LATEST_AUDIT_LOGS_COUNT,
	LATEST_AUDIT_LOGS_POLL_MS,
	useGetLatestAuditLogs,
} from '@/services';
import { formatProfessionalFullName } from '@/utils';
import {
	AUDIT_LOG_ACTION_LABEL,
	AUDIT_LOG_CATEGORY_LABEL,
	formatAuditRelativeTime,
	resolveAuditLogIcon,
} from '@/utils/auditLogDisplay';
import { AdminActivityModal } from './AdminActivityModal';

const ACTIVITY_FEED_CATEGORY_STYLES: Record<
	AuditLogs.AuditLogCategory,
	{ badge: string; dot: string }
> = {
	AUTHENTICATION: {
		badge: 'bg-sky-50 text-sky-600/90 ring-sky-100/80',
		dot: 'bg-sky-200 text-sky-700',
	},
	PROFILE: {
		badge: 'bg-violet-50 text-violet-600/90 ring-violet-100/80',
		dot: 'bg-violet-200 text-violet-700',
	},
	SCHEDULE: {
		badge: 'bg-blue-50/80 text-blue-600/90 ring-blue-100/70',
		dot: 'bg-blue-200 text-blue-700',
	},
	SESSION: {
		badge: 'bg-emerald-50 text-emerald-600/90 ring-emerald-100/80',
		dot: 'bg-emerald-200 text-emerald-700',
	},
	EXTERNAL_API: {
		badge: 'bg-slate-50 text-slate-600/90 ring-slate-100/80',
		dot: 'bg-slate-200 text-slate-600',
	},
	AUTHORIZATION: {
		badge: 'bg-rose-50 text-rose-600/90 ring-rose-100/80',
		dot: 'bg-rose-200 text-rose-700',
	},
	NOTIFICATION: {
		badge: 'bg-cyan-50 text-cyan-600/90 ring-cyan-100/80',
		dot: 'bg-cyan-200 text-cyan-700',
	},
	PATIENT: {
		badge: 'bg-teal-50/80 text-teal-600/90 ring-teal-100/70',
		dot: 'bg-teal-200 text-teal-700',
	},
	MEDICAL_RECORD: {
		badge: 'bg-indigo-50/80 text-indigo-600/90 ring-indigo-100/70',
		dot: 'bg-indigo-200 text-indigo-700',
	},
	UNAVAILABILITY: {
		badge: 'bg-orange-50/80 text-orange-600/90 ring-orange-100/70',
		dot: 'bg-orange-200 text-orange-700',
	},
};

const ActivityFeedSkeleton = () => (
	<div className="space-y-2">
		{Array.from({ length: LATEST_AUDIT_LOGS_COUNT }).map((_, index) => (
			<div
				key={index}
				className="flex animate-pulse flex-col gap-2 rounded-xl border border-stone-100 bg-stone-50/60 p-4"
			>
				<div className="flex items-center gap-2">
					<div className="size-7 rounded-full bg-stone-200" />
					<div className="h-3 flex-1 rounded bg-stone-200" />
					<div className="h-3 w-14 rounded bg-stone-100" />
				</div>
				<div className="h-4 w-2/3 rounded bg-stone-200" />
				<div className="h-3 w-1/2 rounded bg-stone-100" />
			</div>
		))}
	</div>
);

type ActivityFeedItemProps = {
	entry: AuditLogs.AuditLogLatestItem;
};

const ActivityFeedItem = ({ entry }: ActivityFeedItemProps) => {
	const styles = ACTIVITY_FEED_CATEGORY_STYLES[entry.category];
	const Icon = resolveAuditLogIcon(entry.category, entry.action);
	const categoryLabel = AUDIT_LOG_CATEGORY_LABEL[entry.category];
	const actionLabel = AUDIT_LOG_ACTION_LABEL[entry.action];
	const professionalName = entry.professional
		? formatProfessionalFullName(entry.professional)
		: null;

	return (
		<li>
			<article className="rounded-xl border border-stone-100/80 bg-stone-50/30 p-3.5">
				<div className="flex items-center gap-2">
					<div
						className={cn(
							'flex size-6 shrink-0 items-center justify-center rounded-full',
							styles.dot,
						)}
					>
						<Icon
							className="size-3"
							strokeWidth={2.25}
							aria-hidden
						/>
					</div>

					<span
						className={cn(
							'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1',
							styles.badge,
						)}
					>
						{categoryLabel}
					</span>

					<time
						dateTime={entry.occurred_at}
						className="ml-auto shrink-0 text-[10px] font-medium tabular-nums text-stone-400/90"
					>
						{formatAuditRelativeTime(entry.occurred_at)}
					</time>
				</div>

				<p className="mt-2 text-[13px] font-medium leading-snug text-stone-700">
					{actionLabel}
				</p>

				<p className="mt-0.5 truncate text-[11px] text-stone-400">
					{professionalName ?? '—'}
				</p>
			</article>
		</li>
	);
};

export const AdminActivityFeed = () => {
	const [modalOpen, setModalOpen] = useState(false);
	const { data, isLoading, isFetching, isError } = useGetLatestAuditLogs({
		last: LATEST_AUDIT_LOGS_COUNT,
	});

	const entries = data?.data ?? [];

	return (
		<>
			<aside className="group relative flex h-full min-h-0 flex-col overflow-hidden rounded-[1.25rem] border border-stone-100 bg-white p-5 shadow-sm transition-shadow duration-300 group-hover:shadow-[0_12px_40px_rgba(28,25,23,0.12),0_4px_12px_rgba(28,25,23,0.08)]">
				<div className="flex shrink-0 items-start justify-between gap-3">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Activity
								className="size-4 text-stone-500"
								aria-hidden
							/>
							<h2 className="text-base font-bold text-stone-900">
								Atividade recente
							</h2>
						</div>
						<p className="text-xs text-stone-400">
							Últimos {LATEST_AUDIT_LOGS_COUNT} eventos · a cada{' '}
							{LATEST_AUDIT_LOGS_POLL_MS / 1000}s
						</p>
					</div>

					{isFetching && !isLoading ? (
						<Loader2
							className="size-4 shrink-0 animate-spin text-stone-400"
							aria-label="Atualizando"
						/>
					) : (
						<span
							className="relative flex size-2 shrink-0"
							aria-hidden
						>
							<span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-40" />
							<span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
						</span>
					)}
				</div>

				<div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-1">
					{isLoading ? (
						<ActivityFeedSkeleton />
					) : isError ? (
						<p className="rounded-xl border border-rose-100 bg-rose-50/60 px-3 py-4 text-sm text-rose-700">
							Não foi possível carregar a atividade recente.
						</p>
					) : entries.length === 0 ? (
						<p className="rounded-xl border border-dashed border-stone-200 bg-stone-50/60 px-3 py-8 text-center text-sm text-stone-400">
							Nenhum evento registrado ainda.
						</p>
					) : (
						<ul className="space-y-3">
							{entries.map((entry) => (
								<ActivityFeedItem
									key={entry.id}
									entry={entry}
								/>
							))}
						</ul>
					)}
				</div>

				<div
					className={cn(
						'absolute inset-0 z-10 flex items-center justify-center rounded-[1.25rem] bg-stone-900/35 opacity-0 backdrop-blur-[2px] transition-opacity duration-300',
						'shadow-[inset_0_0_32px_rgba(28,25,23,0.18),inset_0_0_64px_rgba(28,25,23,0.1)]',
						'pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100',
						'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
					)}
				>
					<button
						type="button"
						onClick={() => setModalOpen(true)}
						className="flex size-20 items-center justify-center rounded-full border border-white/40 bg-white/20 text-white shadow-md backdrop-blur-sm transition-transform hover:scale-105 hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
						aria-label="Expandir atividade recente"
					>
						<Maximize2
							className="size-10"
							strokeWidth={1.75}
							aria-hidden
						/>
					</button>
				</div>
			</aside>

			<AdminActivityModal
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
			/>
		</>
	);
};
